import { useEffect, useRef, useState } from "react";
import { apiPcf, type PcfForm as PcfFormData, type CombosPcf, type OpcaoProduto, type OpcaoCliente } from "../lib/api";
import { Typeahead } from "../components/Typeahead";
import { useTheme } from "../hooks/useTheme";
import { Logo } from "../components/Logo";
import { logout, type Usuario } from "../lib/auth";
import { MsgModal } from "../components/MsgModal";
import { mascaraDecimal } from "../lib/decimal";
import { EmpenhoMaterialModal } from "../components/EmpenhoMaterialModal";

// atcadpcf / TELA-PCF "Plano de Controle de Fabricação".
// Parte 2a: carrega/exibe (combos + teórico). Parte 2b: edição + gravação
// (Incluir/Alterar), fiel a VER-CONSISTENCIAS, VER-TOLERANCIAS, MOVER-CAMPOS,
// ROTINA-INCLUSAO e botao-alterar. Teórico (qtd/metros/peso) é do open (read-only).

export type PcfFormModo =
  | { kind: "carregar"; numpcf: number }
  | {
      kind: "nova";
      tipo: string;
      numped: number;
      sequen: number;
      codcli: number;
      codpro: number;
      qtd?: number;
      comprimento?: number;
      datent?: string | null;
    };

interface Props {
  usuario: Usuario;
  modo: PcfFormModo;
  onVoltar: () => void;
  onImprimir: (numpcf: number) => void;
  onEtapas: (numpcf: number) => void;
  onAbrir: (numpcf: number) => void; // abrir outra PCF
  onDuplicarCliPro: (o: { numped: number; codcli: number; codpro: number }) => void; // "Duplicar PCF" da PCF nova
}

const nf2 = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt2 = (n: number) => nf2.format(n || 0);

function fmtData(iso: string | null): string {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return "";
  const [a, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${a}`;
}

// Normas que liberam entrada manual de tolerâncias (fiel a NOMASTM-WS LostFocus).
const TOL_MANUAL = new Set(["N/A", "ASTM A554"]);
// As 8 tolerâncias editáveis (campo da tela -> chave do PcfForm).
const TOL_KEYS = [
  "compmin",
  "compmax",
  "tolextMin",
  "tolextMax",
  "tolintMin",
  "tolintMax",
  "tolespMin",
  "tolespMax",
] as const;
type TolKey = (typeof TOL_KEYS)[number];

// Entrada numérica pt-BR: só dígitos + 1 vírgula + '-' inicial (regra de campo numérico).
// Tolerâncias PIC S9(4)V99 / quantidades S9(9)V99 no legado (TELA-PCF L80-87, L101): 2 casas, sinal permitido.
function limparDecimal(v: string, casas = 2): string {
  return mascaraDecimal(v, casas, true);
}
const soDigitos = (v: string) => v.replace(/\D/g, "");

// Campo numérico editável pt-BR (MOVER-CAMPOS lê da tela: QTDPEC/QTDMET/PESO/COMPRIMENTO_FIXO/NUMPED).
// Mantém o texto enquanto digita e grava o número ao sair; 2 casas (PIC S9(9)V99) ou inteiro.
function CampoNum({
  valor,
  casas,
  onCommit,
  readOnly,
  onKeyDown,
  semMilhar,
}: {
  valor: number;
  casas: 0 | 2;
  semMilhar?: boolean; // nº de pedido/PCF: sem separador de milhar
  onCommit: (n: number) => void;
  readOnly?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}) {
  const fmt = (n: number) =>
    new Intl.NumberFormat("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas, useGrouping: !semMilhar }).format(n || 0);
  const [txt, setTxt] = useState(fmt(valor));
  const [editando, setEditando] = useState(false);
  useEffect(() => {
    if (!editando) setTxt(fmt(valor));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor, editando]);
  return (
    <input
      className="num"
      inputMode={casas ? "decimal" : "numeric"}
      value={txt}
      readOnly={readOnly}
      onFocus={() => {
        if (readOnly) return;
        setEditando(true);
        setTxt(String(valor || 0).replace(".", ","));
      }}
      onChange={(e) => setTxt(casas ? limparDecimal(e.target.value, casas).replace("-", "") : soDigitos(e.target.value))}
      onBlur={() => {
        if (readOnly) return;
        const n = Math.trunc((Number(txt.replace(",", ".")) || 0) * 100) / 100;
        onCommit(casas ? n : Math.trunc(n));
        setEditando(false);
      }}
      onKeyDown={onKeyDown}
    />
  );
}
const parseDecBR = (s: string) => Number(s.replace(/\s/g, "").replace(",", ".")) || 0;
const numToBR = (n: number) => String(n ?? 0).replace(".", ",");

export function PcfForm({ usuario, modo, onVoltar, onImprimir, onEtapas, onDuplicarCliPro }: Props) {
  const { theme, toggle } = useTheme();
  const [combos, setCombos] = useState<CombosPcf | null>(null);
  const [f, setF] = useState<PcfFormData | null>(null);
  const [tol, setTol] = useState<Record<TolKey, string>>(() =>
    Object.fromEntries(TOL_KEYS.map((k) => [k, "0"])) as Record<TolKey, string>
  );
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [confirmarCancelar, setConfirmarCancelar] = useState(false);
  const [empenhoAberto, setEmpenhoAberto] = useState(false); // botao-empenho (TELA-PCF.prc:853-862, legado novo)
  // tela-duplicacao: depois do Incluir, pede a PCF antiga para copiar tolerâncias e etapas.
  const [dupDe, setDupDe] = useState<{ numpcf: number; old: string; nomcli: string } | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  const set = (patch: Partial<PcfFormData>) => setF((cur) => (cur ? { ...cur, ...patch } : cur));
  const tolFromF = (x: PcfFormData) =>
    Object.fromEntries(TOL_KEYS.map((k) => [k, numToBR(x[k] as number)])) as Record<TolKey, string>;

  async function carregar(numpcf: number) {
    const dados = await apiPcf.carregar(numpcf);
    setF(dados);
    setTol(tolFromF(dados));
  }

  useEffect(() => {
    void (async () => {
      try {
        setErro(null);
        setCarregando(true);
        const [cmb, dados] = await Promise.all([
          apiPcf.combos(),
          modo.kind === "carregar"
            ? apiPcf.carregar(modo.numpcf)
            : apiPcf.nova({
                tipo: modo.tipo,
                numped: modo.numped,
                sequen: modo.sequen,
                codcli: modo.codcli,
                codpro: modo.codpro,
                qtd: modo.qtd,
                comprimento: modo.comprimento,
                datent: modo.datent,
              }),
        ]);
        setCombos(cmb);
        setF(dados);
        setTol(tolFromF(dados));
      } catch (e) {
        setErro((e as Error).message);
      } finally {
        setCarregando(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const novo = f?.numpcf === 0;
  const pedidoTravado = (f?.tipo === "VENDA" || f?.tipo === "COMPLEMENTAR") && (f?.numped ?? 0) !== 0;
  // TELA-PCF L1385-1391: na base ACOSNOGUEIRA só Incluir/Duplicar/Duplicar PCF (PCF nova) ficam desligados.
  const bloqueiaInclusao = novo && (f?.nomeBd ?? "").toUpperCase() === "ACOSNOGUEIRA";
  const travado = false;
  // T1-NOMASTM-WS LostFocus (L1148-1165): fora de N/A e ASTM A554 só externo e espessura travam
  // (comprimento e interno seguem editáveis — as linhas 1149-1150/1153-1154 estão comentadas).
  const tolEditavel = !travado && TOL_MANUAL.has((f?.nomastm ?? "").trim().toUpperCase());
  const tolLiberada = (k: TolKey) => tolEditavel || k === "compmin" || k === "compmax" || k === "tolintMin" || k === "tolintMax";

  // Enter = Tab (regra 5.1): foca o próximo campo em vez de submeter.
  function enterTab(e: React.KeyboardEvent) {
    if (e.key !== "Enter") return;
    const alvo = e.target as HTMLElement;
    if (alvo.tagName === "TEXTAREA") return;
    e.preventDefault();
    const root = formRef.current;
    if (!root) return;
    const foc = Array.from(
      root.querySelectorAll<HTMLElement>("input,select,textarea,button")
    ).filter((x) => !(x as HTMLInputElement).disabled && x.tabIndex !== -1 && x.offsetParent !== null);
    const i = foc.indexOf(alvo);
    if (i >= 0 && i < foc.length - 1) foc[i + 1]?.focus();
  }

  // Troca de norma -> recalcula tolerâncias (VER-TOLERANCIAS) e ajusta liberação manual.
  async function mudarNorma(v: string) {
    if (!f) return;
    const base = { ...f, nomastm: v };
    setF(base);
    if (!v.trim()) {
      setAviso("Norma não selecionada!"); // L1140-1146
      focar("nomastm");
      return;
    }
    setAviso(null);
    try {
      const r = await apiPcf.tolerancias({
        nomastm: v,
        codastm: base.codastm,
        diaext: base.diaext,
        espessura: base.espessura,
        compmin: base.compmin,
        compmax: base.compmax,
        tolextMin: base.tolextMin,
        tolextMax: base.tolextMax,
        tolespMin: base.tolespMin,
        tolespMax: base.tolespMax,
      });
      const atualizado: PcfFormData = {
        ...base,
        codastm: r.codastm,
        compmin: r.compmin,
        compmax: r.compmax,
        tolextMin: r.tolextMin,
        tolextMax: r.tolextMax,
        tolespMin: r.tolespMin,
        tolespMax: r.tolespMax,
      };
      setF(atualizado);
      setTol(tolFromF(atualizado));
    } catch (e) {
      setErro((e as Error).message);
    }
  }

  // Trocar o produto de uma PCF existente (botao-pesquisa-produto): recalcula
  // diâmetro/espessura/peso e as tolerâncias. Fiel ao SCRIPTLET9 da TELA-PCF.
  async function trocarProduto(codpro: number) {
    if (!f) return;
    try {
      const d = await apiPcf.produtoDados(codpro);
      const peso = (f.qtdmet || 0) * d.pesoTeoricoMetro;
      const base: PcfFormData = {
        ...f,
        codpro: d.codpro,
        nompro: d.nompro,
        diaext: d.diaext,
        espessura: d.espessura,
        peso,
      };
      setF(base);
      if (base.nomastm && base.nomastm.trim()) {
        const r = await apiPcf.tolerancias({
          nomastm: base.nomastm,
          codastm: base.codastm,
          diaext: base.diaext,
          espessura: base.espessura,
          compmin: base.compmin,
          compmax: base.compmax,
          tolextMin: base.tolextMin,
          tolextMax: base.tolextMax,
          tolespMin: base.tolespMin,
          tolespMax: base.tolespMax,
        });
        const atualizado: PcfFormData = {
          ...base,
          codastm: r.codastm,
          compmin: r.compmin,
          compmax: r.compmax,
          tolextMin: r.tolextMin,
          tolextMax: r.tolextMax,
          tolespMin: r.tolespMin,
          tolespMax: r.tolespMax,
        };
        setF(atualizado);
        setTol(tolFromF(atualizado));
      }
    } catch (e) {
      setErro((e as Error).message);
    }
  }

  // Campo de tolerância editável: mantém string + número em f.
  function mudarTol(k: TolKey, v: string) {
    const s = limparDecimal(v);
    setTol((cur) => ({ ...cur, [k]: s }));
    set({ [k]: parseDecBR(s) } as Partial<PcfFormData>);
  }

  async function buscarFornecedor(cod?: number) {
    const codfor = cod ?? f?.codfor ?? 0;
    if (!f || !codfor) {
      set({ nomfor: "" });
      return;
    }
    try {
      const r = await apiPcf.fornecedor(codfor);
      if (r.erro) {
        setAviso(r.erro);
        set({ nomfor: r.erro === "Fornecedor Inativo!" ? r.nomfor : "" });
        focar("codfor");
      } else {
        setAviso(null);
        set({ nomfor: r.nomfor });
      }
    } catch (e) {
      setErro((e as Error).message);
    }
  }

  function focar(campo: string) {
    if (!campo) return;
    const el = formRef.current?.querySelector<HTMLElement>(`[data-campo="${campo}"]`);
    el?.focus();
  }

  async function gravar() {
    if (!f || salvando) return;
    setSalvando(true);
    setErro(null);
    setAviso(null);
    setOk(null);
    try {
      const r = novo ? await apiPcf.incluir(f) : await apiPcf.alterar(f);
      if (!r.ok) {
        setAviso(r.mensagem);
        focar(r.campo);
        return;
      }
      if (novo) {
        onEtapas(r.numpcf); // botao-incluir L2745-2747: abre PCF_ETAPAS da PCF incluída
        return;
      }
      setOk("PCF alterada com sucesso.");
      await carregar(r.numpcf);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  async function finalizar() {
    if (!f || novo || salvando) return;
    setSalvando(true);
    setErro(null);
    setAviso(null);
    setOk(null);
    try {
      const r = await apiPcf.finalizar(f.numpcf);
      if (!r.ok) {
        setAviso(r.mensagem);
        return;
      }
      onImprimir(f.numpcf); // FINALIZAR L1764: CALL "IMPRIMIR"
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  // botao-duplicar (L1206-1217): ROTINA-INCLUSAO e, incluída, a tela-duplicacao.
  async function duplicarNova() {
    if (!f || salvando) return;
    setSalvando(true);
    setErro(null);
    setAviso(null);
    try {
      const r = await apiPcf.incluir(f);
      if (!r.ok) {
        setAviso(r.mensagem);
        focar(r.campo);
        return;
      }
      setDupDe({ numpcf: r.numpcf, old: "", nomcli: "" });
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  async function conferirPcfAntiga() {
    if (!dupDe || !Number(dupDe.old)) return;
    const r = await apiPcf.duplicarDeCliente(Number(dupDe.old));
    if (r.erro) {
      setAviso(r.erro);
      setDupDe({ ...dupDe, nomcli: "" });
      return;
    }
    setAviso(null);
    setDupDe({ ...dupDe, nomcli: r.nomcli });
  }

  // tela-duplicacao botao-gravar: copia da antiga e segue para as etapas da nova (L1216).
  async function gravarDuplicacao() {
    if (!dupDe) return;
    const old = Number(dupDe.old) || 0;
    if (old) {
      const r = await apiPcf.duplicarDe(dupDe.numpcf, old);
      if (!r.ok) {
        setAviso(r.mensagem);
        return;
      }
    }
    onEtapas(dupDe.numpcf);
  }

  // botao-imprimir (L2497-2519): põe em produção e imprime.
  async function imprimir() {
    if (!f || novo || salvando) return;
    setSalvando(true);
    setErro(null);
    try {
      const r = await apiPcf.imprimir(f.numpcf);
      if (!r.ok) {
        setAviso(r.mensagem);
        return;
      }
      onImprimir(f.numpcf);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  async function cancelar() {
    if (!f || novo) return;
    setConfirmarCancelar(false);
    setSalvando(true);
    setErro(null);
    setAviso(null);
    setOk(null);
    try {
      const r = await apiPcf.cancelar(f.numpcf);
      if (!r.ok) {
        setAviso(r.mensagem);
        return;
      }
      setOk(`PCF ${f.numpcf} cancelada.`);
      await carregar(f.numpcf);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  // Combo editável (desabilita quando travado/ro).
  const Combo = ({
    valor,
    opcoes,
    onChange,
    campo,
    ro,
  }: {
    valor: string;
    opcoes: string[];
    onChange: (v: string) => void;
    campo?: string;
    ro?: boolean;
  }) => {
    const lista = valor && !opcoes.includes(valor) ? [valor, ...opcoes] : opcoes;
    return (
      <select
        value={valor}
        data-campo={campo}
        disabled={ro ?? travado}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={enterTab}
      >
        {!valor && <option value="" />}
        {lista.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  };

  const RO = ({ v }: { v: string | number }) => <input value={String(v ?? "")} readOnly />;

  if (carregando) {
    return (
      <div className="app">
        <div className="status-loading">Carregando…</div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand">
          <Logo />
          <h1>
            Plano de Controle de Fabricação
            <small>PCF — {novo ? "Nova" : `Nº ${f?.numpcf ?? ""}`}</small>
          </h1>
        </div>
        <span className="spacer" />
        <div className="user-badge" title={`Usuário: ${usuario.nomusu}`}>
          <span className="user-avatar">{usuario.apelid.charAt(0).toUpperCase()}</span>
          <span className="user-nome">{usuario.nomusu.split(" ")[0]}</span>
        </div>
        <button className="btn-secundario" onClick={onVoltar} title="Voltar ao PCP">
          ← PCP
        </button>
        <button className="btn-secundario" onClick={toggle} title={theme === "light" ? "Modo escuro" : "Modo claro"}>{theme === "light" ? "Modo escuro" : "Modo claro"}</button>
        <button className="btn-secundario" onClick={logout} title="Sair">Sair</button>
      </header>

      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />
      <MsgModal texto={aviso} tipo="aviso" onClose={() => setAviso(null)} />
      <MsgModal texto={ok} tipo="ok" onClose={() => setOk(null)} />

      {f && (
        <div className="pcf-form" ref={formRef}>
          {bloqueiaInclusao && (
            <div className="pcf-aviso-trava">
              Base <b>{f.nomeBd}</b> — inclusão e duplicação de PCF desabilitadas.
            </div>
          )}

          {/* Identificação */}
          <section className="pcf-sec">
            <h2>Identificação</h2>
            <div className="pcf-grid">
              <label className="pcf-campo pcf-sm">
                <span>Nº PCF</span>
                <RO v={novo ? "—" : String(f.numpcf)} />
              </label>
              <label className="pcf-campo pcf-sm">
                <span>Tipo de Solicitação</span>
                <RO v={f.tipo} />
              </label>
              <label className="pcf-campo pcf-sm">
                <span>Situação da PCF</span>
                <RO v={f.situac} />
              </label>
              <label className="pcf-campo pcf-sm">
                <span>Item</span>
                <input
                  value={f.nroite}
                  disabled={travado}
                  onChange={(e) => set({ nroite: e.target.value })}
                  onKeyDown={enterTab}
                />
              </label>
              <label className="pcf-campo pcf-sm">
                <span>Data Emissão</span>
                <RO v={fmtData(f.datemi)} />
              </label>
              <label className="pcf-campo pcf-sm">
                <span>Data Entrega</span>
                <input
                  type="date"
                  data-campo="datent"
                  value={f.datent ?? ""}
                  disabled={travado}
                  onChange={(e) => set({ datent: e.target.value || null })}
                  onKeyDown={enterTab}
                />
              </label>
              <label className="pcf-campo pcf-sm">
                <span>Nº Pedido</span>
                {/* MOSTRAR-CAMPOS L2723-2729: Nº do pedido só fica travado em VENDA/COMPLEMENTAR com pedido */}
                <CampoNum valor={f.numped} casas={0} semMilhar readOnly={pedidoTravado} onCommit={(n) => set({ numped: n })} onKeyDown={enterTab} />
              </label>
              <label className="pcf-campo">
                <span>Emissor</span>
                <RO v={f.nomusu} />
              </label>
            </div>
          </section>

          {/* Cliente */}
          <section className="pcf-sec">
            <h2>Cliente</h2>
            <div className="pcf-grid">
              <label className="pcf-campo pcf-lg">
                <span>Cliente</span>
                <RO v={f.nomcli} />
              </label>
              <label className="pcf-campo">
                <span>Vendedor / Solicitante</span>
                <Combo valor={f.nomven} opcoes={combos?.vendedores ?? []} ro={pedidoTravado} onChange={(v) => set({ nomven: v })} />
              </label>
              <label className="pcf-campo">
                <span>Tipo de PCF</span>
                <Combo
                  valor={f.tipoPcf}
                  opcoes={combos?.tiposPcf ?? []}
                  campo="tipoPcf"
                  onChange={(v) => set({ tipoPcf: v })}
                />
              </label>
              <label className="pcf-campo">
                <span>Pedido de Compra</span>
                <input
                  value={f.numPc}
                  disabled={travado}
                  onChange={(e) => set({ numPc: e.target.value })}
                  onKeyDown={enterTab}
                />
              </label>
            </div>
          </section>

          {/* Produto */}
          <section className="pcf-sec">
            <h2>Produto</h2>
            <div className="pcf-grid">
              <label className="pcf-campo pcf-lg">
                <span>Produto</span>
                {novo || travado ? (
                  <RO v={f.nompro} />
                ) : (
                  <Typeahead<OpcaoProduto>
                    placeholder="Trocar produto…"
                    valorInicial={f.nompro}
                    buscar={apiPcf.buscarProduto}
                    rotulo={(p) => `${p.codpro} — ${p.nompro}`}
                    onEscolher={(p) => void trocarProduto(p.codpro)}
                  />
                )}
              </label>
              <label className="pcf-campo pcf-sm">
                <span>Diâmetro Externo</span>
                <input className="num" value={fmt2(f.diaext)} readOnly />
              </label>
              <label className="pcf-campo pcf-sm">
                <span>Espessura</span>
                <input className="num" value={fmt2(f.espessura)} readOnly />
              </label>
              <label className="pcf-campo">
                <span>Norma</span>
                <Combo valor={f.nomastm} opcoes={combos?.normas ?? []} campo="nomastm" onChange={(v) => void mudarNorma(v)} />
              </label>
              <label className="pcf-campo">
                <span>Visual</span>
                <Combo valor={f.nomaca} opcoes={combos?.acabamentos ?? []} onChange={(v) => set({ nomaca: v })} />
              </label>
            </div>
          </section>

          {/* Quantidades (teórico calculado na abertura; editáveis — MOVER-CAMPOS L2406-2417) */}
          <section className="pcf-sec">
            <h2>Quantidades</h2>
            <div className="pcf-grid">
              <label className="pcf-campo pcf-sm">
                <span>Qtd Peças (pcs)</span>
                <CampoNum valor={f.qtdpec} casas={2} onCommit={(n) => set({ qtdpec: n })} onKeyDown={enterTab} />
              </label>
              <label className="pcf-campo pcf-sm">
                <span>Metros (Teórico) (m)</span>
                <CampoNum valor={f.qtdmet} casas={2} onCommit={(n) => set({ qtdmet: n })} onKeyDown={enterTab} />
              </label>
              <label className="pcf-campo pcf-sm">
                <span>Peso (Teórico) (kg)</span>
                <CampoNum valor={f.peso} casas={2} onCommit={(n) => set({ peso: n })} onKeyDown={enterTab} />
              </label>
              <label className="pcf-campo pcf-sm">
                <span>Comprimento Solicitado</span>
                <CampoNum valor={f.comprimentoFixo} casas={2} onCommit={(n) => set({ comprimentoFixo: n })} onKeyDown={enterTab} />
              </label>
            </div>
          </section>

          {/* Tolerâncias dimensionais */}
          <section className="pcf-sec">
            <h2>
              Tolerâncias Dimensionais (mm)
              {!tolEditavel && !travado && <small className="pcf-hint"> (externo e espessura definidos pela norma)</small>}
            </h2>
            <table className="pcf-tol">
              <thead>
                <tr>
                  <th />
                  <th className="num">Mínimo</th>
                  <th className="num">Máximo</th>
                </tr>
              </thead>
              <tbody>
                {(
                  [
                    ["Comprimento", "compmin", "compmax"],
                    ["Diâmetro Externo", "tolextMin", "tolextMax"],
                    ["Diâmetro Interno", "tolintMin", "tolintMax"],
                    ["Espessura", "tolespMin", "tolespMax"],
                  ] as [string, TolKey, TolKey][]
                ).map(([rot, kmin, kmax]) => (
                  <tr key={rot}>
                    <td>{rot}</td>
                    <td className="num">
                      <input
                        className="num pcf-tol-in"
                        value={tolLiberada(kmin) ? tol[kmin] : fmt2(f[kmin] as number)}
                        readOnly={!tolLiberada(kmin)}
                        onChange={(e) => mudarTol(kmin, e.target.value)}
                        onKeyDown={enterTab}
                      />
                    </td>
                    <td className="num">
                      <input
                        className="num pcf-tol-in"
                        value={tolLiberada(kmax) ? tol[kmax] : fmt2(f[kmax] as number)}
                        readOnly={!tolLiberada(kmax)}
                        onChange={(e) => mudarTol(kmax, e.target.value)}
                        onKeyDown={enterTab}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* Expedição */}
          <section className="pcf-sec">
            <h2>Expedição</h2>
            <div className="pcf-grid">
              <label className="pcf-campo">
                <span>Frete</span>
                <Combo valor={f.frete} opcoes={combos?.fretes ?? []} onChange={(v) => set({ frete: v })} />
              </label>
              <label className="pcf-campo">
                <span>Embalagem</span>
                <Combo valor={f.opcoes} opcoes={combos?.embalagens ?? []} onChange={(v) => set({ opcoes: v })} />
              </label>
              <div className="pcf-checks">
                <label className="pcf-check">
                  <input
                    type="checkbox"
                    checked={f.certificado}
                    disabled={travado}
                    onChange={(e) => set({ certificado: e.target.checked })}
                  />
                  <span>Certificado?</span>
                </label>
                <label className="pcf-check">
                  <input
                    type="checkbox"
                    checked={f.urgente}
                    disabled={travado}
                    onChange={(e) => set({ urgente: e.target.checked })}
                  />
                  <span>PCF Urgente</span>
                </label>
                <label className="pcf-check">
                  <input
                    type="checkbox"
                    checked={f.etiquetar}
                    disabled={travado}
                    onChange={(e) => set({ etiquetar: e.target.checked })}
                  />
                  <span>Etiquetar</span>
                </label>
              </div>
            </div>
          </section>

          {/* Rastreabilidade */}
          <section className="pcf-sec">
            <h2>Rastreabilidade</h2>
            <div className="pcf-grid">
              <label className="pcf-campo pcf-sm">
                <span>O.P.</span>
                <input
                  className="num"
                  inputMode="numeric"
                  value={f.numeop ? String(f.numeop) : ""}
                  disabled={travado}
                  onChange={(e) => set({ numeop: Number(soDigitos(e.target.value)) || 0 })}
                  onKeyDown={enterTab}
                />
              </label>
              <label className="pcf-campo pcf-sm">
                <span>Lote</span>
                <input
                  className="num"
                  inputMode="numeric"
                  value={f.numlot ? String(f.numlot) : ""}
                  disabled={travado}
                  onChange={(e) => set({ numlot: Number(soDigitos(e.target.value)) || 0 })}
                  onKeyDown={enterTab}
                />
              </label>
              <label className="pcf-campo pcf-sm">
                <span>Corrida</span>
                <input value={f.corrida} disabled={travado} onChange={(e) => set({ corrida: e.target.value })} onKeyDown={enterTab} />
              </label>
              <label className="pcf-campo pcf-sm">
                <span>N.F. Entrada</span>
                <input value={f.nfEntrada} disabled={travado} onChange={(e) => set({ nfEntrada: e.target.value })} onKeyDown={enterTab} />
              </label>
              <label className="pcf-campo pcf-sm">
                <span>Gaveteiro</span>
                <input value={f.gaveteiro} disabled={travado} onChange={(e) => set({ gaveteiro: e.target.value })} onKeyDown={enterTab} />
              </label>
              <label className="pcf-campo pcf-sm">
                <span>Cód. Fornecedor</span>
                <input
                  className="num"
                  inputMode="numeric"
                  data-campo="codfor"
                  value={f.codfor ? String(f.codfor) : ""}
                  disabled={travado}
                  onChange={(e) => set({ codfor: Number(soDigitos(e.target.value)) || 0 })}
                  onBlur={() => void buscarFornecedor()}
                  onKeyDown={enterTab}
                />
              </label>
              <label className="pcf-campo pcf-lg">
                <span>Fornecedor</span>
                <Typeahead<OpcaoCliente>
                  placeholder="Pesquisar fornecedor…"
                  valorInicial={f.nomfor}
                  buscar={apiPcf.buscarFornecedor}
                  rotulo={(c) => `${c.codigo} — ${c.nome}`}
                  onEscolher={(c) => {
                    set({ codfor: c.codigo });
                    void buscarFornecedor(c.codigo);
                  }}
                />
              </label>
            </div>
          </section>

          {/* Observação */}
          <section className="pcf-sec">
            <h2>Observações Gerais</h2>
            <textarea
              value={f.observ}
              disabled={travado}
              rows={3}
              onChange={(e) => set({ observ: e.target.value })}
            />
          </section>

          {dupDe && (
            <div className="pcf-modal-bg">
              <div className="pcf-modal pcf-modal-estreito" onClick={(e) => e.stopPropagation()}>
                <div className="pcf-modal-topo">
                  <b>PCF {dupDe.numpcf} incluída — duplicar de qual PCF?</b>
                </div>
                <div className="pcf-grid">
                  <label className="pcf-campo pcf-sm">
                    <span>Nº PCF antiga</span>
                    <input
                      className="num"
                      inputMode="numeric"
                      autoFocus
                      value={dupDe.old}
                      onChange={(e) => setDupDe({ ...dupDe, old: soDigitos(e.target.value), nomcli: "" })}
                      onBlur={() => void conferirPcfAntiga()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          void conferirPcfAntiga();
                        }
                      }}
                    />
                  </label>
                  <label className="pcf-campo pcf-lg">
                    <span>Cliente</span>
                    <input value={dupDe.nomcli} readOnly tabIndex={-1} />
                  </label>
                </div>
                <p className="pcf-hint">Copia comprimentos, tolerâncias e etapas da PCF antiga para a nova.</p>
                <div className="pcf-acoes">
                  <button className="btn-acao" onClick={() => void gravarDuplicacao()}>
                    ✓ Gravar
                  </button>
                  <span className="spacer" />
                  <button className="btn-secundario" onClick={() => onEtapas(dupDe.numpcf)}>
                    Sair
                  </button>
                </div>
              </div>
            </div>
          )}

          {empenhoAberto && <EmpenhoMaterialModal numpcf={f.numpcf} onClose={() => setEmpenhoAberto(false)} />}

          {confirmarCancelar && (
            <div className="plan-confirm">
              <span>
                Confirma o <b>cancelamento</b> da PCF <b>{f.numpcf}</b>?
              </span>
              <button className="btn-acao plan-del" onClick={() => void cancelar()}>
                Confirmar
              </button>
              <button className="btn-secundario" onClick={() => setConfirmarCancelar(false)}>
                Voltar
              </button>
            </div>
          )}

          {/* Ações — TELA-PCF L1385-1398: PCF nova = Incluir/Duplicar/Duplicar PCF; existente = Alterar/Etapas/Finalizar/Imprimir/Cancelar */}
          <div className="pcf-acoes">
            {novo ? (
              <>
                <button className="btn-acao" disabled={bloqueiaInclusao || salvando} onClick={() => void gravar()}>
                  {salvando ? "Gravando…" : "Incluir"}
                </button>
                <button className="btn-secundario" disabled={bloqueiaInclusao || salvando} onClick={() => void duplicarNova()}>
                  Duplicar Etapas
                </button>
                <button
                  className="btn-secundario"
                  disabled={bloqueiaInclusao || salvando}
                  onClick={() => onDuplicarCliPro({ numped: f.numped, codcli: f.codcli, codpro: f.codpro })}
                >
                  Duplicar PCF
                </button>
              </>
            ) : (
              <>
                <button className="btn-acao" disabled={salvando} onClick={() => void gravar()}>
                  {salvando ? "Gravando…" : "Alterar"}
                </button>
                <button className="btn-secundario" onClick={() => onEtapas(f.numpcf)}>
                  Etapas
                </button>
                <button className="btn-secundario" onClick={() => setEmpenhoAberto(true)}>
                  Empenho
                </button>
                <button className="btn-secundario" disabled={salvando} onClick={() => void finalizar()}>
                  Finalizar Lançamento
                </button>
                <button className="btn-secundario" disabled={salvando} onClick={() => void imprimir()}>
                  Imprimir
                </button>
                <button className="btn-secundario plan-del" disabled={salvando} onClick={() => setConfirmarCancelar(true)}>
                  Cancelar
                </button>
              </>
            )}
            <span className="spacer" />
            <button className="btn-secundario" onClick={onVoltar}>
              Sair
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
